import React from 'react';
import { Layout } from '../components/Layout';
import { usePageMetadata } from '../hooks/usePageMetadata';

const PrivacyPage = () => {
  usePageMetadata();

  return (
    <Layout>
      <div className="bg-white min-h-screen py-16 px-4">
        <div className="max-w-4xl mx-auto prose prose-slate">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Privacy Policy</h1>
          
          <p className="text-slate-500 mb-8">Last updated: February 2025</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">1. Introduction</h2>
            <p className="text-slate-600">
              Stay Pair Homes ("we," "our," or "us") is committed to protecting your privacy. This Privacy 
              Policy explains how we collect, use, disclose, and safeguard your information when you use 
              our website and services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">2. Information We Collect</h2>
            
            <h3 className="text-xl font-medium text-slate-900 mt-4">Personal Information</h3>
            <p className="text-slate-600">
              We may collect personal information you provide directly, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
              <li>Name, email address, and phone number</li>
              <li>Account credentials</li>
              <li>Profile information</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Communications with us or other users</li>
            </ul>

            <h3 className="text-xl font-medium text-slate-900 mt-4">Automatically Collected Information</h3>
            <p className="text-slate-600">
              When you use our Service, we automatically collect:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
              <li>Device information (browser type, operating system)</li>
              <li>IP address and location data</li>
              <li>Usage patterns and preferences</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">3. How We Use Your Information</h2>
            <p className="text-slate-600">
              We use collected information to:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Monitor and analyze usage patterns</li>
              <li>Detect, prevent, and address fraud or security issues</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">4. Information Sharing</h2>
            <p className="text-slate-600">
              We may share your information with:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
              <li>Other users (limited profile information to facilitate rentals)</li>
              <li>Service providers (payment processors, hosting, analytics)</li>
              <li>Legal authorities when required by law</li>
              <li>Business partners (only with your consent)</li>
            </ul>
            <p className="text-slate-600 mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">5. Data Security</h2>
            <p className="text-slate-600">
              We implement appropriate technical and organizational measures to protect your personal 
              information, including encryption, secure servers, and access controls. However, no 
              method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">6. Your Rights</h2>
            <p className="text-slate-600">
              Depending on your location, you may have rights to:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Delete your data</li>
              <li>Object to or restrict processing</li>
              <li>Data portability</li>
              <li>Withdraw consent</li>
            </ul>
            <p className="text-slate-600 mt-4">
              To exercise these rights, contact us at privacy@staypairhomes.com
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">7. Cookies</h2>
            <p className="text-slate-600">
              We use cookies and similar technologies to enhance your experience, analyze usage, and 
              remember your preferences. You can manage cookie preferences through your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">8. Children's Privacy</h2>
            <p className="text-slate-600">
              Our Service is not intended for children under 18. We do not knowingly collect information 
              from children. If you believe we have collected such information, please contact us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">9. California Privacy Rights</h2>
            <p className="text-slate-600">
              California residents have additional rights under the CCPA, including the right to know 
              what personal information we collect, delete your information, and opt-out of sales 
              (we do not sell personal information).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">10. Changes to This Policy</h2>
            <p className="text-slate-600">
              We may update this Privacy Policy periodically. We will notify you of material changes by 
              posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900">11. Contact Us</h2>
            <p className="text-slate-600">
              For questions about this Privacy Policy, contact us at{' '}
              <a href="mailto:privacy@staypairhomes.com" className="text-trust-blue hover:underline">
                privacy@staypairhomes.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPage;
